import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    LinearProgress,
    Typography,
    Box,
    Alert,
} from "@mui/material";
import { http } from "Utils/Http/Http";

const ExportProgressModal = ({ open, exportJobId, onClose, onComplete }) => {
    const [status, setStatus] = useState("pending");
    const [progress, setProgress] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [processedRecords, setProcessedRecords] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);
    const [filename, setFilename] = useState("");

    const downloadStartedRef = React.useRef(false);
    const intervalRef = React.useRef(null);

    useEffect(() => {
        // Reset download flag when modal opens
        if (open) {
            downloadStartedRef.current = false;
        }

        // Don't start polling if we don't have an exportJobId yet
        if (!open || !exportJobId) return;

        const pollStatus = async () => {
            // Guard: Don't poll if download has already started
            if (downloadStartedRef.current) {
                return;
            }

            try {
                const response = await http.get(
                    `api/v1/admin/participations/export/${exportJobId}/status`
                );
                const data = response.data;

                const now = new Date();
                console.log(`[${now.toISOString().split('T')[1]}] Poll:`, {
                    status: data.status,
                    progress: data.progress,
                    total: data.total_records,
                    percentage: data.progress_percentage
                });

                setStatus(data.status);
                setProgress(data.progress_percentage);
                setTotalRecords(data.total_records);
                setProcessedRecords(data.progress);
                setErrorMessage(data.error_message);

                if (data.status === "completed" && !downloadStartedRef.current) {
                    console.log("Setting download started flag");
                    downloadStartedRef.current = true; // Prevent multiple downloads

                    // IMMEDIATELY stop polling
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }

                    // Set filename and trigger download via status change
                    setFilename(data.filename);
                    setStatus("downloading");
                } else if (data.status === "failed") {
                    // Stop polling on failure
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }

                    setTimeout(() => {
                        onClose();
                    }, 3000);
                }
            } catch (error) {
                console.error("Error fetching export status:", error);
                setErrorMessage("Failed to fetch export status");
            }
        };

        // Call pollStatus first, then start interval (poll every 500ms for faster updates)
        pollStatus().then(() => {
            // Only start interval if we haven't started download yet
            if (!downloadStartedRef.current && open && exportJobId) {
                intervalRef.current = setInterval(pollStatus, 500);
            }
        });

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [open, exportJobId, onClose, onComplete]);

    // Separate effect to handle download - runs only once when status becomes "downloading"
    useEffect(() => {
        if (status === "downloading" && filename && exportJobId) {
            console.log("Triggering download for:", filename);

            http.get(
                `api/v1/admin/participations/export/${exportJobId}/download`,
                { responseType: 'blob' }
            ).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                setTimeout(() => {
                    onComplete?.();
                    onClose();
                }, 1000);
            }).catch(error => {
                console.error("Download error:", error);
                setErrorMessage("Failed to download file");
            });
        }
    }, [status, filename, exportJobId, onComplete, onClose]);

    const getStatusMessage = () => {
        switch (status) {
            case "pending":
                return "Export queued. Waiting to start...";
            case "processing":
                return "Exporting participations...";
            case "downloading":
                return "Export completed! Downloading...";
            case "completed":
                return "Export completed!";
            case "failed":
                return "Export failed";
            default:
                return "Processing...";
        }
    };

    return (
        <Dialog open={open} onClose={() => { }} maxWidth="sm" fullWidth>
            <DialogTitle>
                {status === "completed" ? "Export Completed" : "Exporting Data"}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ width: "100%", mt: 2 }}>
                    {!exportJobId ? (
                        <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Starting export...
                            </Typography>
                            <LinearProgress
                                sx={{ my: 2, height: 8, borderRadius: 4 }}
                            />
                        </>
                    ) : errorMessage ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {getStatusMessage()}
                            </Typography>

                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ my: 2, height: 8, borderRadius: 4 }}
                            />

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" color="text.secondary">
                                    {processedRecords.toLocaleString()} /{" "}
                                    {totalRecords.toLocaleString()} records
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {progress}%
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ExportProgressModal;
